import { DEADLINE_URGENCY_DEFAULT, DEFAULT_SNOOZE_HOURS, systemTimeZone, type MatrixScope } from "@app/core";
import type { ThemeMode } from "@app/ui";
import { create } from "zustand";
import { getServices } from "../platform.js";
import { BLOCK_TINT_DIFF_DEFAULT, BLOCK_TINT_DIFF_MAX } from "../features/task-colors.js";
import { DEFAULT_LANGUAGE, isAppLanguage, type AppLanguage } from "../lib/language.js";
import { touchPreferencesUpdatedAt } from "../lib/preferences.js";
import { useCalendarNavStore } from "./calendarNav.store.js";
import {
  clampTextScale,
  isAppTextFont,
  TEXT_FONT_DEFAULT,
  TEXT_SCALE_DEFAULT,
  type AppTextFont,
} from "../lib/typography.js";

/** Sentinel stored when the user explicitly chooses floating / device-local. */
const FLOATING = "floating";

/** Debounce handle for the calendar-zoom persist (see setCalendarHourHeight). */
let hourHeightPersist: ReturnType<typeof setTimeout> | null = null;
/** Debounce handle for the sidebar-width persist (see setSidebarWidth). */
let sidebarWidthPersist: ReturnType<typeof setTimeout> | null = null;
/** Debounce handle for the mobile-sheet-height persist (drag gesture). */
let mobileSheetHeightPersist: ReturnType<typeof setTimeout> | null = null;
let settingsHydrateInflight: Promise<void> | null = null;

export type TimeFormat = "24h" | "12h";
/** Default left edge for Week and X-Days when focusing the current period. */
export type MultiDayFocus = "current_week" | "current_day" | "current_day_minus_one";
const MULTI_DAY_FOCUSES: MultiDayFocus[] = ["current_week", "current_day", "current_day_minus_one"];
/** Global keyboard-shortcut actions the user can rebind (C9). Values are
 *  normalized "chord" strings (see lib/shortcut-chord.ts) — most are bare keys,
 *  `undo` defaults to the `mod+z` combo. */
export type ShortcutAction = "newTask" | "today" | "priorities" | "calNext" | "calPrev" | "help" | "undo";
export const DEFAULT_SHORTCUTS: Record<ShortcutAction, string> = {
  newTask: "n", today: "t", priorities: "p", calNext: "k", calPrev: "j", help: "?", undo: "mod+z",
};
/** Sidebar parts the user can hide entirely (core nav included). */
export type SidebarPart = "today" | "calendar" | "tasks" | "priorities" | "inbox" | "search" | "claude" | "filters";
const DEFAULT_SIDEBAR_VISIBLE: Record<SidebarPart, boolean> = {
  today: true, calendar: true, tasks: true, priorities: true, inbox: true, search: true, claude: true, filters: true,
};
/** Reorderable top nav parts. The Filters group has its own section reordering.
 *  (`sanitizeSidebarOrder` appends missing defaults, so a part added here shows
 *  up for existing users with a stored order.) */
const DEFAULT_SIDEBAR_ORDER: SidebarPart[] = ["today", "calendar", "tasks", "priorities", "inbox", "search", "claude"];
/** Collapsible sidebar sections: the whole filter group + each filter list. */
export type SidebarSectionKey = "filters" | "topics" | "labels" | "priority" | "urgency" | "calendars";
const DEFAULT_SIDEBAR_COLLAPSED: Record<SidebarSectionKey, boolean> = {
  filters: false, topics: false, labels: false, priority: false, urgency: false, calendars: false,
};
/** The reorderable sidebar filter sections (drag-to-reorder; persisted + backed up). */
export type FilterSectionKey = "topics" | "labels" | "priority" | "urgency" | "calendars";
const DEFAULT_FILTER_ORDER: FilterSectionKey[] = ["topics", "labels", "priority", "urgency", "calendars"];
/** How completing/reopening a task propagates to/from its subtasks. "ask" pops a
 *  per-action popup; the others map onto core's `SubtaskRollupMode`. */
export type SubtaskRollup = "two_way" | "children_to_parent" | "parent_to_children" | "ask";
/** Route the app opens on (the index redirect target), relative to "/". */
export type DefaultView = "today" | "calendar/month" | "calendar/week" | "calendar/day" | "tasks";
/** Which calendar sub-view the user last had open (drives the Calendar nav tab
 *  + search "View" jump so both return to where the user left off). */
export type CalendarView = "month" | "week" | "days" | "day";
/** Route-specific display exclusions. These do not affect sidebar filters or
 *  other views; they are applied only by the route that owns the view. */
export type ExcludableView = "month" | "today";
/** Topic visibility also supports the full Priorities view; calendar visibility
 * remains Month/Today-only. */
export type TopicExcludableView = ExcludableView | "priorities";
/** Mobile create/edit sheet: how a topic button renders its content. */
export type MobileTopicDisplay = "icons" | "icons+text" | "text";
const MOBILE_TOPIC_DISPLAYS: MobileTopicDisplay[] = ["icons", "icons+text", "text"];
/** Desktop create/edit dialog skin: the classic paper card or the modern (v2)
 *  look that follows the mobile sheet's visual language. */
export type DesktopFormVariant = "paper" | "modern";
const DESKTOP_FORM_VARIANTS: DesktopFormVariant[] = ["paper", "modern"];
/** `MatrixScope` ("off" | "tasks" | "tasks_events") and its `matrixEnabledFor`
 *  gate live in `@app/core` (`tasks/matrix.ts`) so pure modules (filters,
 *  sorts) share them without importing this store. Re-exported here for the
 *  settings-flavored call sites. */
export { matrixEnabledFor, type MatrixScope } from "@app/core";
const MATRIX_SCOPES: MatrixScope[] = ["off", "tasks", "tasks_events"];

/** Bounds for the deadline-derived urgency day thresholds (Settings → Tasks). */
export const DEADLINE_URGENCY_DAY_MIN = 0;
export const DEADLINE_URGENCY_DAY_MAX = 365;

/** Bounds for the persisted vertical zoom (px per hour) in week/day grids. */
export const HOUR_HEIGHT_MIN = 24;
export const HOUR_HEIGHT_MAX = 160;
export const HOUR_HEIGHT_DEFAULT = 48;
/** Bounds for the persisted X-Days view day count. */
export const DAY_COUNT_MIN = 1;
export const DAY_COUNT_MAX = 14;
export const DAY_COUNT_DEFAULT = 4;
/** Bounds for the persisted sidebar width (px; drag the sidebar's right edge).
 *  Dragging below MIN snaps to MINI — an icon-only rail (labels/filters hidden). */
export const SIDEBAR_WIDTH_MINI = 68;
export const SIDEBAR_WIDTH_MIN = 168;
export const SIDEBAR_WIDTH_MAX = 480;
export const SIDEBAR_WIDTH_DEFAULT = 224;
/** Bounds for the persisted mobile create/edit sheet partial-state height
 *  (fraction of the viewport; State #1 "full" is a separate flag). */
export const MOBILE_SHEET_HEIGHT_MIN = 0.1;
export const MOBILE_SHEET_HEIGHT_MAX = 0.9;
export const MOBILE_SHEET_HEIGHT_DEFAULT = 0.1;

/** DEBUG tunables for the mobile calendar's horizontal finger-pan (Settings →
 *  General "Touch pan (debug)" sliders). Defaults are looser than the old
 *  hardcoded values so the grid feels less "sticky" out of the box.
 *    - amp:    track moves this much MORE than the finger (1 = 1:1, higher = looser)
 *    - flingMs: how far the release velocity projects forward → extra days flicked
 *    - flipPx:  finger travel needed to page one day on release (lower = easier)
 *    - maxStep: cap on a single swipe's committed day step */
export const TOUCH_PAN_AMP_MIN = 1;
export const TOUCH_PAN_AMP_MAX = 3;
export const TOUCH_PAN_AMP_DEFAULT = 1.5;
export const TOUCH_FLING_MS_MIN = 0;
export const TOUCH_FLING_MS_MAX = 400;
export const TOUCH_FLING_MS_DEFAULT = 90;
export const TOUCH_FLIP_PX_MIN = 20;
export const TOUCH_FLIP_PX_MAX = 200;
export const TOUCH_FLIP_PX_DEFAULT = 60;
export const TOUCH_MAX_STEP_MIN = 1;
export const TOUCH_MAX_STEP_MAX = 20;
export const TOUCH_MAX_STEP_DEFAULT = 9;

/** Bounds + default for the duration (minutes) seeded onto a NEW timed entry
 *  when the creation point doesn't imply a length (e.g. the "+" button rather
 *  than a drag-to-create selection). */
export const DEFAULT_DURATION_MIN = 5;
export const DEFAULT_DURATION_MAX = 1440;
export const DEFAULT_DURATION_DEFAULT = 60;

/** Default local hour for the Priorities time-ladder "afternoon" rung
 *  (morning/evening reuse the core snooze anchor defaults). */
export const SNOOZE_AFTERNOON_HOUR_DEFAULT = 14;

/** First run only (no persisted choice): phone-sized viewports start with the
 *  sidebar closed — the collapsed brand chip re-opens it. Any user toggle is
 *  persisted and wins from then on. */
function defaultSidebarOpen(): boolean {
  return typeof window === "undefined" || window.innerWidth >= 640;
}

/** Clamp a raw sidebar width: values under the MINI/MIN midpoint snap to the
 *  icon-only MINI rail; everything else clamps into [MIN, MAX]. */
function clampSidebarWidth(raw: unknown): number {
  const n = typeof raw === "string" ? Number(raw) : typeof raw === "number" ? raw : NaN;
  if (!Number.isFinite(n)) return SIDEBAR_WIDTH_DEFAULT;
  if (n < (SIDEBAR_WIDTH_MINI + SIDEBAR_WIDTH_MIN) / 2) return SIDEBAR_WIDTH_MINI;
  return Math.min(SIDEBAR_WIDTH_MAX, Math.max(SIDEBAR_WIDTH_MIN, Math.round(n)));
}

function clampInt(raw: unknown, min: number, max: number, fallback: number): number {
  const n = typeof raw === "string" ? Number(raw) : typeof raw === "number" ? raw : NaN;
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

function clampFloat(raw: unknown, min: number, max: number, fallback: number): number {
  const n = typeof raw === "string" ? Number(raw) : typeof raw === "number" ? raw : NaN;
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

/** How far ahead the Today agenda looks (days shown incl. today). */
export const AGENDA_DAY_CHOICES = [3, 7, 14] as const;
export const AGENDA_DAYS_DEFAULT = 7;

function parseAgendaDays(raw: unknown): number {
  const n = typeof raw === "string" ? Number(raw) : typeof raw === "number" ? raw : NaN;
  return (AGENDA_DAY_CHOICES as readonly number[]).includes(n) ? n : AGENDA_DAYS_DEFAULT;
}

// The two Today-view preferences share one ordered persistence lane. Their UI
// updates optimistically, but repository writes are asynchronous; without a
// lane, a slower earlier click can finish last and become the value restored on
// the next launch even though React correctly shows the later click.
let agendaPreferenceWrite: Promise<void> = Promise.resolve();
let agendaDaysRevision = 0;
let agendaShowEmptyDaysRevision = 0;

function persistAgendaPreference(write: () => Promise<void>): Promise<void> {
  const pending = agendaPreferenceWrite.catch(() => undefined).then(write);
  agendaPreferenceWrite = pending;
  return pending;
}

interface SettingsState {
  /** True once hydrate() has run — the index redirect waits on this. */
  loaded: boolean;
  anthropicApiKey: string | null;
  weekStartsOn: 0 | 1;
  multiDayFocus: MultiDayFocus;
  /** Default tz applied to NEW tasks. A zone name, or null = floating/device-local.
   *  Unset by the user ⇒ resolves to the device's IANA zone (DST-correct default). */
  defaultTz: string | null;
  /** Calendar assigned to NEW tasks by default. Seeded by the calendars store to
   *  the first calendar on first run; null until that bootstrap completes. */
  defaultCalendarId: string | null;
  /** 24-hour ("13:00") vs 12-hour ("1:00 PM") time display. */
  timeFormat: TimeFormat;
  /** Route the app opens on by default. */
  defaultView: DefaultView;
  /** Reminder lead-times (minutes-before-due) seeded onto NEW tasks. */
  defaultReminderMinutes: number[];
  /** Local hour-of-day (0–23) the snooze "morning" presets anchor to. */
  snoozeMorningHour: number;
  /** Local hour-of-day (0–23) the snooze "this evening" preset anchors to. */
  snoozeEveningHour: number;
  /** Local hour-of-day (0–23) the Priorities time-ladder "afternoon" rung
   *  anchors to (morning/evening reuse the snooze anchors above). */
  snoozeAfternoonHour: number;
  /** Days the Today agenda shows (incl. today) — one of AGENDA_DAY_CHOICES. */
  agendaDays: number;
  /** Whether Today renders upcoming days that have nothing scheduled. */
  agendaShowEmptyDays: boolean;
  /** Colour scheme. Mirrored into the ThemeProvider by Shell. */
  theme: ThemeMode;
  /** App language for localized UI surfaces. */
  language: AppLanguage;
  /** App-wide typography scale; text roles keep their relative sizes. */
  textScale: number;
  /** App-wide text font stack preset. */
  textFont: AppTextFont;
  /** Last calendar sub-view the user opened (month/week/days/day). */
  lastCalendarView: CalendarView;
  /** Vertical zoom of the week/day grid: px per hour (Shift+scroll the axis). */
  calendarHourHeight: number;
  /** Days shown in the flexible X-Days calendar view. */
  calendarDayCount: number;
  /** Strength of the pastel-tint difference between task and event blocks
   *  (0 = identical, higher = tasks lighter / events more saturated). */
  blockTintDiff: number;
  /** Whether single-color blocks get a slight gradient sheen (off = flat fill).
   *  Does not affect the topic+custom two-color blend, which is always a mix. */
  blockGradient: boolean;
  /** How subtask completion rolls up between a parent and its children. */
  subtaskRollup: SubtaskRollup;
  /** Where the 3×3 priority/urgency matrix is enabled (off / tasks / tasks+events). */
  matrixScope: MatrixScope;
  /** Auto-populate the matrix urgency axis from a task's deadline when the user
   *  hasn't placed it in the matrix (Eisenhower "urgency = time pressure"). */
  deadlineUrgency: boolean;
  /** Day-distance threshold (inclusive) at/under which a deadline reads as HIGH urgency. */
  deadlineUrgencyHighDays: number;
  /** Day-distance threshold (inclusive) at/under which a deadline reads as MEDIUM
   *  urgency; beyond it reads as LOW. */
  deadlineUrgencyMediumDays: number;
  /** Mobile create/edit sheet: topic-button content (icons / icons+text / text). */
  mobileTopicDisplay: MobileTopicDisplay;
  /** Create/edit forms: allow choosing multiple topics (else single). */
  mobileTopicMulti: boolean;
  /** Desktop create/edit dialog skin (paper = classic, modern = mobile-styled v2). */
  desktopFormVariant: DesktopFormVariant;
  /** Mobile create/edit sheet: partial-state height (fraction of viewport). */
  mobileSheetHeight: number;
  /** Mobile: open the feedback composer automatically after a system screenshot. */
  mobileScreenshotFeedback: boolean;
  /** Mobile: show the ← Filters / slide to expand / Inbox → hint bar at the top of calendar views. */
  showCalendarSwipeHint: boolean;
  /** DEBUG: mobile calendar horizontal-pan tuning (see constants above). */
  touchPanAmp: number;
  touchPanFlingMs: number;
  touchPanFlipPx: number;
  touchPanMaxStep: number;
  /** Whether NEW entries default to an event (vs a task). */
  defaultEntryIsEvent: boolean;
  /** Default duration (minutes) for a new timed entry created without an implied length. */
  defaultDurationMin: number;
  /** Reminder lead-times (minutes-before-due) seeded onto NEW events.
   *  Tasks use `defaultReminderMinutes`; events default to none. */
  defaultEventReminderMinutes: number[];
  /** Developer options unlocked: surfaces the Developer settings category +
   *  debug cards. DEVICE-LOCAL — never backed up. */
  developerMode: boolean;
  /** First-run onboarding has been dismissed. Synced/backed up with preferences. */
  onboardingComplete: boolean;
  /** Last first-run onboarding step reached. Synced/backed up with preferences. */
  onboardingStep: string | null;
  /** User-rebindable global keyboard shortcuts (action → single key). */
  shortcuts: Record<ShortcutAction, string>;
  /** Which optional sidebar parts are shown. */
  sidebarVisible: Record<SidebarPart, boolean>;
  /** Priorities nav manual visibility; null follows whether matrix scope is on. */
  prioritiesNavVisibleOverride: boolean | null;
  /** User-set order of the top-level sidebar parts. */
  sidebarNavOrder: SidebarPart[];
  /** Which sidebar sections are collapsed. */
  sidebarCollapsed: Record<SidebarSectionKey, boolean>;
  /** Sidebar width in px (drag-resize on its right border). */
  sidebarWidth: number;
  /** Whole-sidebar visibility (the <</>> collapse toggle next to the title). */
  sidebarOpen: boolean;
  /** Order of the sidebar filter sections (drag-to-reorder). */
  sidebarFilterOrder: FilterSectionKey[];
  /** User-set order (ids) of the entries within each sidebar list. Empty = the
   *  list's natural order. Drag-to-reorder; persisted + backed up. */
  topicOrder: string[];
  /** Per-parent drag order for child topics. Key = parent topic id, value = ordered child ids. */
  topicChildOrder: Record<string, string[]>;
  labelOrder: string[];
  calendarOrder: string[];
  /** Topic/calendar ids hidden only from Month view. */
  monthExcludedTopicIds: string[];
  monthExcludedCalendarIds: string[];
  /** Topic/calendar ids hidden only from Today view. */
  todayExcludedTopicIds: string[];
  todayExcludedCalendarIds: string[];
  /** Topic ids hidden from the entire Priorities view. */
  prioritiesExcludedTopicIds: string[];
  hydrate: () => Promise<void>;
  setApiKey: (key: string) => Promise<void>;
  setWeekStartsOn: (v: 0 | 1) => Promise<void>;
  setMultiDayFocus: (v: MultiDayFocus) => Promise<void>;
  setDefaultTz: (tz: string | null) => Promise<void>;
  setDefaultCalendarId: (id: string | null) => Promise<void>;
  setTimeFormat: (v: TimeFormat) => Promise<void>;
  setDefaultView: (v: DefaultView) => Promise<void>;
  setDefaultReminderMinutes: (v: number[]) => Promise<void>;
  setSnoozeHours: (morningHour: number, eveningHour: number, afternoonHour?: number) => Promise<void>;
  setTheme: (v: ThemeMode) => Promise<void>;
  setLanguage: (v: AppLanguage) => Promise<void>;
  setTextScale: (v: number) => Promise<void>;
  setTextFont: (v: AppTextFont) => Promise<void>;
  setLastCalendarView: (v: CalendarView) => Promise<void>;
  setCalendarHourHeight: (px: number) => Promise<void>;
  setCalendarDayCount: (n: number) => Promise<void>;
  setBlockTintDiff: (v: number) => Promise<void>;
  setBlockGradient: (v: boolean) => Promise<void>;
  setSubtaskRollup: (v: SubtaskRollup) => Promise<void>;
  setMatrixScope: (v: MatrixScope) => Promise<void>;
  setDeadlineUrgency: (v: boolean) => Promise<void>;
  setDeadlineUrgencyHighDays: (v: number) => Promise<void>;
  setDeadlineUrgencyMediumDays: (v: number) => Promise<void>;
  setMobileTopicDisplay: (v: MobileTopicDisplay) => Promise<void>;
  setMobileTopicMulti: (v: boolean) => Promise<void>;
  setDesktopFormVariant: (v: DesktopFormVariant) => Promise<void>;
  setMobileSheetHeight: (fraction: number) => Promise<void>;
  setMobileScreenshotFeedback: (v: boolean) => Promise<void>;
  setShowCalendarSwipeHint: (v: boolean) => Promise<void>;
  setTouchPanAmp: (v: number) => Promise<void>;
  setTouchPanFlingMs: (v: number) => Promise<void>;
  setTouchPanFlipPx: (v: number) => Promise<void>;
  setTouchPanMaxStep: (v: number) => Promise<void>;
  setDefaultEntryIsEvent: (v: boolean) => Promise<void>;
  setDefaultDurationMin: (v: number) => Promise<void>;
  setDefaultEventReminderMinutes: (v: number[]) => Promise<void>;
  setDeveloperMode: (v: boolean) => Promise<void>;
  setOnboardingComplete: (v: boolean) => Promise<void>;
  setOnboardingStep: (v: string | null) => Promise<void>;
  /** Reset the user-facing scalar preferences to their defaults (does NOT touch
   *  lists, sidebar layout, account/sync, or developer mode). */
  resetPreferences: () => Promise<void>;
  setShortcut: (action: ShortcutAction, key: string) => Promise<void>;
  resetShortcuts: () => Promise<void>;
  setSidebarVisible: (part: SidebarPart, visible: boolean) => Promise<void>;
  resetPrioritiesNavVisibility: () => Promise<void>;
  setSidebarNavOrder: (order: SidebarPart[]) => Promise<void>;
  setSidebarCollapsed: (section: SidebarSectionKey, collapsed: boolean) => Promise<void>;
  setSidebarWidth: (px: number) => Promise<void>;
  setSidebarOpen: (open: boolean) => Promise<void>;
  setSidebarFilterOrder: (order: FilterSectionKey[]) => Promise<void>;
  setTopicOrder: (order: string[]) => Promise<void>;
  setTopicChildOrder: (parentId: string, order: string[]) => Promise<void>;
  setLabelOrder: (order: string[]) => Promise<void>;
  setCalendarOrder: (order: string[]) => Promise<void>;
  setViewExcludedTopics: (view: TopicExcludableView, ids: string[]) => Promise<void>;
  setViewExcludedCalendars: (view: ExcludableView, ids: string[]) => Promise<void>;
  setAgendaDays: (v: number) => Promise<void>;
  setAgendaShowEmptyDays: (v: boolean) => Promise<void>;
  /** Apply a backup's curated preferences (shortcuts + sidebar prefs); persists. */
  applyBackupPreferences: (
    prefs: import("@app/core").BackupPreferences,
    opts?: { touch?: boolean },
  ) => Promise<void>;
}

const CALENDAR_VIEWS: CalendarView[] = ["month", "week", "days", "day"];

const DEFAULT_VIEWS: DefaultView[] = ["today", "calendar/month", "calendar/week", "calendar/day", "tasks"];

const ROLLUP_MODES: SubtaskRollup[] = ["two_way", "children_to_parent", "parent_to_children", "ask"];

export const useSettingsStore = create<SettingsState>((set) => ({
  loaded: false,
  anthropicApiKey: null,
  weekStartsOn: 1,
  multiDayFocus: "current_week",
  defaultTz: systemTimeZone(),
  defaultCalendarId: null,
  timeFormat: "24h",
  defaultView: "today",
  defaultReminderMinutes: [0],
  snoozeMorningHour: DEFAULT_SNOOZE_HOURS.morningHour,
  snoozeEveningHour: DEFAULT_SNOOZE_HOURS.eveningHour,
  snoozeAfternoonHour: SNOOZE_AFTERNOON_HOUR_DEFAULT,
  agendaDays: AGENDA_DAYS_DEFAULT,
  agendaShowEmptyDays: false,
  theme: "system",
  language: DEFAULT_LANGUAGE,
  textScale: TEXT_SCALE_DEFAULT,
  textFont: TEXT_FONT_DEFAULT,
  lastCalendarView: "month",
  calendarHourHeight: HOUR_HEIGHT_DEFAULT,
  calendarDayCount: DAY_COUNT_DEFAULT,
  blockTintDiff: BLOCK_TINT_DIFF_DEFAULT,
  blockGradient: true,
  subtaskRollup: "two_way",
  matrixScope: "tasks_events",
  deadlineUrgency: DEADLINE_URGENCY_DEFAULT.enabled,
  deadlineUrgencyHighDays: DEADLINE_URGENCY_DEFAULT.highWithinDays,
  deadlineUrgencyMediumDays: DEADLINE_URGENCY_DEFAULT.mediumWithinDays,
  mobileTopicDisplay: "icons+text",
  mobileTopicMulti: true,
  desktopFormVariant: "paper",
  mobileSheetHeight: MOBILE_SHEET_HEIGHT_DEFAULT,
  mobileScreenshotFeedback: true,
  showCalendarSwipeHint: true,
  touchPanAmp: TOUCH_PAN_AMP_DEFAULT,
  touchPanFlingMs: TOUCH_FLING_MS_DEFAULT,
  touchPanFlipPx: TOUCH_FLIP_PX_DEFAULT,
  touchPanMaxStep: TOUCH_MAX_STEP_DEFAULT,
  defaultEntryIsEvent: true,
  defaultDurationMin: DEFAULT_DURATION_DEFAULT,
  defaultEventReminderMinutes: [],
  developerMode: false,
  onboardingComplete: false,
  onboardingStep: null,
  shortcuts: DEFAULT_SHORTCUTS,
  sidebarVisible: DEFAULT_SIDEBAR_VISIBLE,
  prioritiesNavVisibleOverride: null,
  sidebarNavOrder: DEFAULT_SIDEBAR_ORDER,
  sidebarCollapsed: DEFAULT_SIDEBAR_COLLAPSED,
  sidebarWidth: SIDEBAR_WIDTH_DEFAULT,
  sidebarOpen: true,
  sidebarFilterOrder: DEFAULT_FILTER_ORDER,
  topicOrder: [],
  topicChildOrder: {},
  labelOrder: [],
  calendarOrder: [],
  monthExcludedTopicIds: [],
  monthExcludedCalendarIds: [],
  todayExcludedTopicIds: [],
  todayExcludedCalendarIds: [],
  prioritiesExcludedTopicIds: [],

  hydrate() {
    if (settingsHydrateInflight) return settingsHydrateInflight;
    settingsHydrateInflight = hydrateSettingsOnce(set).finally(() => { settingsHydrateInflight = null; });
    return settingsHydrateInflight;
  },

  async setApiKey(key: string) {
    const { settings } = getServices();
    await settings.set("anthropic_api_key", key);
    set({ anthropicApiKey: key });
  },

  async setWeekStartsOn(v: 0 | 1) {
    const { settings } = getServices();
    await settings.set("week_starts_on", String(v));
    await touchPreferencesUpdatedAt();
    set({ weekStartsOn: v });
  },

  async setMultiDayFocus(v: MultiDayFocus) {
    if (useSettingsStore.getState().multiDayFocus === v) return;
    set({ multiDayFocus: v });
    await getServices().settings.set("multi_day_focus", v);
    await touchPreferencesUpdatedAt();
    useCalendarNavStore.getState().clearAnchors(["week", "days"]);
  },

  async setDefaultTz(tz: string | null) {
    const { settings } = getServices();
    await settings.set("default_tz", tz ?? FLOATING);
    await touchPreferencesUpdatedAt();
    set({ defaultTz: tz });
  },

  async setDefaultCalendarId(id: string | null) {
    const { settings } = getServices();
    await settings.set("default_calendar_id", id ?? "");
    await touchPreferencesUpdatedAt();
    set({ defaultCalendarId: id });
  },

  async setTimeFormat(v: TimeFormat) {
    const { settings } = getServices();
    await settings.set("time_format", v);
    await touchPreferencesUpdatedAt();
    set({ timeFormat: v });
  },

  async setDefaultView(v: DefaultView) {
    const { settings } = getServices();
    await settings.set("default_view", v);
    await touchPreferencesUpdatedAt();
    set({ defaultView: v });
  },

  async setDefaultReminderMinutes(v: number[]) {
    const { settings } = getServices();
    await settings.set("default_reminder_minutes", JSON.stringify(v));
    await touchPreferencesUpdatedAt();
    set({ defaultReminderMinutes: v });
  },

  async setSnoozeHours(morningHour: number, eveningHour: number, afternoonHour?: number) {
    const m = clampInt(morningHour, 0, 23, DEFAULT_SNOOZE_HOURS.morningHour);
    const e = clampInt(eveningHour, 0, 23, DEFAULT_SNOOZE_HOURS.eveningHour);
    const a = clampInt(
      afternoonHour ?? useSettingsStore.getState().snoozeAfternoonHour,
      0, 23, SNOOZE_AFTERNOON_HOUR_DEFAULT,
    );
    const { settings } = getServices();
    await settings.set("snooze_morning_hour", String(m));
    await settings.set("snooze_evening_hour", String(e));
    await settings.set("snooze_afternoon_hour", String(a));
    await touchPreferencesUpdatedAt();
    set({ snoozeMorningHour: m, snoozeEveningHour: e, snoozeAfternoonHour: a });
  },

  async setAgendaDays(v: number) {
    const val = parseAgendaDays(v);
    if (useSettingsStore.getState().agendaDays === val) return;
    // Keep rapid option changes ordered from React's point of view. Deferring
    // this until after two repository writes let an older click finish last
    // and overwrite the newer selection in memory.
    agendaDaysRevision++;
    set({ agendaDays: val });
    await persistAgendaPreference(async () => {
      await getServices().settings.set("agenda_days", String(val));
      await touchPreferencesUpdatedAt();
    });
  },

  async setAgendaShowEmptyDays(v: boolean) {
    if (useSettingsStore.getState().agendaShowEmptyDays === v) return;
    agendaShowEmptyDaysRevision++;
    set({ agendaShowEmptyDays: v });
    await persistAgendaPreference(async () => {
      await getServices().settings.set("agenda_show_empty_days", String(v));
      await touchPreferencesUpdatedAt();
    });
  },

  async setTheme(v: ThemeMode) {
    const { settings } = getServices();
    await settings.set("theme", v);
    await touchPreferencesUpdatedAt();
    set({ theme: v });
  },

  async setLanguage(v: AppLanguage) {
    const language = isAppLanguage(v) ? v : DEFAULT_LANGUAGE;
    if (useSettingsStore.getState().language === language) return;
    set({ language });
    await getServices().settings.set("language", language);
    await touchPreferencesUpdatedAt();
  },

  async setTextScale(v: number) {
    const textScale = clampTextScale(v);
    if (useSettingsStore.getState().textScale === textScale) return;
    set({ textScale });
    await getServices().settings.set("text_scale", String(textScale));
    await touchPreferencesUpdatedAt();
  },

  async setTextFont(v: AppTextFont) {
    const textFont = isAppTextFont(v) ? v : TEXT_FONT_DEFAULT;
    if (useSettingsStore.getState().textFont === textFont) return;
    set({ textFont });
    await getServices().settings.set("text_font", textFont);
    await touchPreferencesUpdatedAt();
  },

  async setLastCalendarView(v: CalendarView) {
    if (useSettingsStore.getState().lastCalendarView === v) return;
    const { settings } = getServices();
    await settings.set("last_calendar_view", v);
    await touchPreferencesUpdatedAt();
    set({ lastCalendarView: v });
  },

  async setCalendarHourHeight(px: number) {
    const v = clampInt(px, HOUR_HEIGHT_MIN, HOUR_HEIGHT_MAX, HOUR_HEIGHT_DEFAULT);
    if (useSettingsStore.getState().calendarHourHeight === v) return;
    set({ calendarHourHeight: v });
    // Set by a continuous wheel gesture (one call per tick) — state applies
    // optimistically above; persist once after the gesture settles instead of
    // one settings write (desktop: an IPC + SQLite hop) per tick.
    if (hourHeightPersist) clearTimeout(hourHeightPersist);
    hourHeightPersist = setTimeout(() => {
      hourHeightPersist = null;
      void getServices().settings.set("calendar_hour_height", String(v));
      void touchPreferencesUpdatedAt();
    }, 400);
  },

  async setCalendarDayCount(n: number) {
    const v = clampInt(n, DAY_COUNT_MIN, DAY_COUNT_MAX, DAY_COUNT_DEFAULT);
    if (useSettingsStore.getState().calendarDayCount === v) return;
    set({ calendarDayCount: v });
    await getServices().settings.set("calendar_day_count", String(v));
    await touchPreferencesUpdatedAt();
  },

  async setBlockTintDiff(v: number) {
    const clamped = clampFloat(v, 0, BLOCK_TINT_DIFF_MAX, BLOCK_TINT_DIFF_DEFAULT);
    if (useSettingsStore.getState().blockTintDiff === clamped) return;
    set({ blockTintDiff: clamped });
    await getServices().settings.set("block_tint_diff", String(clamped));
    await touchPreferencesUpdatedAt();
  },

  async setBlockGradient(v: boolean) {
    if (useSettingsStore.getState().blockGradient === v) return;
    set({ blockGradient: v });
    await getServices().settings.set("block_gradient", String(v));
    await touchPreferencesUpdatedAt();
  },

  async setSubtaskRollup(v: SubtaskRollup) {
    const { settings } = getServices();
    await settings.set("subtask_rollup", v);
    await touchPreferencesUpdatedAt();
    set({ subtaskRollup: v });
  },

  async setMatrixScope(v: MatrixScope) {
    const prev = useSettingsStore.getState().matrixScope;
    if (prev === v) return;
    const { settings } = getServices();
    set({ matrixScope: v });
    try {
      await settings.set("matrix_scope", v);
      await touchPreferencesUpdatedAt();
      // Carry priorities across the mode switch: tasks leaving scope snapshot
      // their live matrix-derived priority into the flag; tasks entering scope
      // honor a flag set while the matrix was off by dropping the stale
      // placement. Dynamic import — a static one would cycle with tasks.store.
      // The preferences-sync path below applies matrixScope WITHOUT this
      // migration on purpose: the originating device migrates and its task
      // writes sync over.
      const { applyMatrixScopeSwitch } = await import("./matrix-scope-switch.js");
      await applyMatrixScopeSwitch(prev, v);
    } catch (error) {
      // Scope and task migration are one user-visible change. Restore the old
      // mode if either write fails so flags and placements are never read under
      // mismatched rules. Best-effort persistence aligns the next boot too.
      set({ matrixScope: prev });
      try {
        await settings.set("matrix_scope", prev);
        await touchPreferencesUpdatedAt();
      } catch { /* Preserve the original failure; in-memory state is safe. */ }
      throw error;
    }
  },

  async setDeadlineUrgency(v: boolean) {
    if (useSettingsStore.getState().deadlineUrgency === v) return;
    set({ deadlineUrgency: v });
    await getServices().settings.set("deadline_urgency", String(v));
    await touchPreferencesUpdatedAt();
  },

  async setDeadlineUrgencyHighDays(v: number) {
    const val = clampInt(v, DEADLINE_URGENCY_DAY_MIN, DEADLINE_URGENCY_DAY_MAX, DEADLINE_URGENCY_DEFAULT.highWithinDays);
    if (useSettingsStore.getState().deadlineUrgencyHighDays === val) return;
    set({ deadlineUrgencyHighDays: val });
    await getServices().settings.set("deadline_urgency_high_days", String(val));
    await touchPreferencesUpdatedAt();
  },

  async setDeadlineUrgencyMediumDays(v: number) {
    const val = clampInt(v, DEADLINE_URGENCY_DAY_MIN, DEADLINE_URGENCY_DAY_MAX, DEADLINE_URGENCY_DEFAULT.mediumWithinDays);
    if (useSettingsStore.getState().deadlineUrgencyMediumDays === val) return;
    set({ deadlineUrgencyMediumDays: val });
    await getServices().settings.set("deadline_urgency_medium_days", String(val));
    await touchPreferencesUpdatedAt();
  },

  async setMobileTopicDisplay(v: MobileTopicDisplay) {
    if (useSettingsStore.getState().mobileTopicDisplay === v) return;
    set({ mobileTopicDisplay: v });
    await getServices().settings.set("mobile_topic_display", v);
    await touchPreferencesUpdatedAt();
  },

  async setMobileTopicMulti(v: boolean) {
    if (useSettingsStore.getState().mobileTopicMulti === v) return;
    set({ mobileTopicMulti: v });
    await getServices().settings.set("mobile_topic_multi", String(v));
    await touchPreferencesUpdatedAt();
  },

  async setDesktopFormVariant(v: DesktopFormVariant) {
    if (useSettingsStore.getState().desktopFormVariant === v) return;
    set({ desktopFormVariant: v });
    await getServices().settings.set("desktop_form_variant", v);
    await touchPreferencesUpdatedAt();
  },

  async setMobileSheetHeight(fraction: number) {
    const v = clampFloat(fraction, MOBILE_SHEET_HEIGHT_MIN, MOBILE_SHEET_HEIGHT_MAX, MOBILE_SHEET_HEIGHT_DEFAULT);
    if (useSettingsStore.getState().mobileSheetHeight === v) return;
    // Set by a continuous drag gesture — state applies optimistically; persist
    // once after the gesture settles (same pattern as setCalendarHourHeight).
    set({ mobileSheetHeight: v });
    if (mobileSheetHeightPersist) clearTimeout(mobileSheetHeightPersist);
    mobileSheetHeightPersist = setTimeout(() => {
      mobileSheetHeightPersist = null;
      void getServices().settings.set("mobile_sheet_height", String(v));
      void touchPreferencesUpdatedAt();
    }, 400);
  },

  async setMobileScreenshotFeedback(v: boolean) {
    if (useSettingsStore.getState().mobileScreenshotFeedback === v) return;
    set({ mobileScreenshotFeedback: v });
    await getServices().settings.set("mobile_screenshot_feedback", String(v));
    await touchPreferencesUpdatedAt();
  },

  async setShowCalendarSwipeHint(v: boolean) {
    if (useSettingsStore.getState().showCalendarSwipeHint === v) return;
    set({ showCalendarSwipeHint: v });
    await getServices().settings.set("show_calendar_swipe_hint", String(v));
    await touchPreferencesUpdatedAt();
  },

  async setTouchPanAmp(v: number) {
    const val = clampFloat(v, TOUCH_PAN_AMP_MIN, TOUCH_PAN_AMP_MAX, TOUCH_PAN_AMP_DEFAULT);
    if (useSettingsStore.getState().touchPanAmp === val) return;
    set({ touchPanAmp: val });
    await getServices().settings.set("touch_pan_amp", String(val));
  },

  async setTouchPanFlingMs(v: number) {
    const val = clampInt(v, TOUCH_FLING_MS_MIN, TOUCH_FLING_MS_MAX, TOUCH_FLING_MS_DEFAULT);
    if (useSettingsStore.getState().touchPanFlingMs === val) return;
    set({ touchPanFlingMs: val });
    await getServices().settings.set("touch_pan_fling_ms", String(val));
  },

  async setTouchPanFlipPx(v: number) {
    const val = clampInt(v, TOUCH_FLIP_PX_MIN, TOUCH_FLIP_PX_MAX, TOUCH_FLIP_PX_DEFAULT);
    if (useSettingsStore.getState().touchPanFlipPx === val) return;
    set({ touchPanFlipPx: val });
    await getServices().settings.set("touch_pan_flip_px", String(val));
  },

  async setTouchPanMaxStep(v: number) {
    const val = clampInt(v, TOUCH_MAX_STEP_MIN, TOUCH_MAX_STEP_MAX, TOUCH_MAX_STEP_DEFAULT);
    if (useSettingsStore.getState().touchPanMaxStep === val) return;
    set({ touchPanMaxStep: val });
    await getServices().settings.set("touch_pan_max_step", String(val));
  },

  async setDefaultEntryIsEvent(v: boolean) {
    if (useSettingsStore.getState().defaultEntryIsEvent === v) return;
    set({ defaultEntryIsEvent: v });
    await getServices().settings.set("default_entry_is_event", String(v));
    await touchPreferencesUpdatedAt();
  },

  async setDefaultDurationMin(v: number) {
    const val = clampInt(v, DEFAULT_DURATION_MIN, DEFAULT_DURATION_MAX, DEFAULT_DURATION_DEFAULT);
    if (useSettingsStore.getState().defaultDurationMin === val) return;
    set({ defaultDurationMin: val });
    await getServices().settings.set("default_duration_min", String(val));
    await touchPreferencesUpdatedAt();
  },

  async setDefaultEventReminderMinutes(v: number[]) {
    set({ defaultEventReminderMinutes: v });
    await getServices().settings.set("default_event_reminder_minutes", JSON.stringify(v));
    await touchPreferencesUpdatedAt();
  },

  async setDeveloperMode(v: boolean) {
    if (useSettingsStore.getState().developerMode === v) return;
    set({ developerMode: v });
    await getServices().settings.set("developer_mode", String(v));
  },

  async setOnboardingComplete(v: boolean) {
    if (useSettingsStore.getState().onboardingComplete === v) return;
    set({ onboardingComplete: v });
    await getServices().settings.set("onboarding_complete", String(v));
    await touchPreferencesUpdatedAt();
  },

  async setOnboardingStep(v: string | null) {
    if (useSettingsStore.getState().onboardingStep === v) return;
    set({ onboardingStep: v });
    await getServices().settings.set("onboarding_step", v ?? "");
    await touchPreferencesUpdatedAt();
  },

  async resetPreferences() {
    const { settings } = getServices();
    const agendaDaysResetRevision = ++agendaDaysRevision;
    const agendaShowEmptyResetRevision = ++agendaShowEmptyDaysRevision;
    const agendaReset = persistAgendaPreference(async () => {
      await settings.set("agenda_days", String(AGENDA_DAYS_DEFAULT));
      await settings.set("agenda_show_empty_days", "false");
    });
    const patch: Partial<SettingsState> = {
      weekStartsOn: 1,
      multiDayFocus: "current_week",
      defaultTz: systemTimeZone(),
      timeFormat: "24h",
      defaultView: "today",
      defaultReminderMinutes: [0],
      defaultEventReminderMinutes: [],
      defaultEntryIsEvent: true,
      defaultDurationMin: DEFAULT_DURATION_DEFAULT,
      snoozeMorningHour: DEFAULT_SNOOZE_HOURS.morningHour,
      snoozeEveningHour: DEFAULT_SNOOZE_HOURS.eveningHour,
      snoozeAfternoonHour: SNOOZE_AFTERNOON_HOUR_DEFAULT,
      agendaDays: AGENDA_DAYS_DEFAULT,
      agendaShowEmptyDays: false,
      theme: "system",
      language: DEFAULT_LANGUAGE,
      textScale: TEXT_SCALE_DEFAULT,
      textFont: TEXT_FONT_DEFAULT,
      blockTintDiff: BLOCK_TINT_DIFF_DEFAULT,
      blockGradient: true,
      subtaskRollup: "two_way",
      matrixScope: "tasks_events",
      deadlineUrgency: DEADLINE_URGENCY_DEFAULT.enabled,
      deadlineUrgencyHighDays: DEADLINE_URGENCY_DEFAULT.highWithinDays,
      deadlineUrgencyMediumDays: DEADLINE_URGENCY_DEFAULT.mediumWithinDays,
      mobileTopicDisplay: "icons+text",
      mobileTopicMulti: true,
      desktopFormVariant: "paper",
      showCalendarSwipeHint: true,
      mobileScreenshotFeedback: true,
      touchPanAmp: TOUCH_PAN_AMP_DEFAULT,
      touchPanFlingMs: TOUCH_FLING_MS_DEFAULT,
      touchPanFlipPx: TOUCH_FLIP_PX_DEFAULT,
      touchPanMaxStep: TOUCH_MAX_STEP_DEFAULT,
    };
    await Promise.all([
      settings.set("week_starts_on", "1"),
      settings.set("multi_day_focus", "current_week"),
      settings.set("default_tz", systemTimeZone()),
      settings.set("time_format", "24h"),
      settings.set("default_view", "today"),
      settings.set("default_reminder_minutes", JSON.stringify([0])),
      settings.set("default_event_reminder_minutes", JSON.stringify([])),
      settings.set("default_entry_is_event", "true"),
      settings.set("default_duration_min", String(DEFAULT_DURATION_DEFAULT)),
      settings.set("snooze_morning_hour", String(DEFAULT_SNOOZE_HOURS.morningHour)),
      settings.set("snooze_evening_hour", String(DEFAULT_SNOOZE_HOURS.eveningHour)),
      settings.set("snooze_afternoon_hour", String(SNOOZE_AFTERNOON_HOUR_DEFAULT)),
      agendaReset,
      settings.set("theme", "system"),
      settings.set("language", DEFAULT_LANGUAGE),
      settings.set("text_scale", String(TEXT_SCALE_DEFAULT)),
      settings.set("text_font", TEXT_FONT_DEFAULT),
      settings.set("block_tint_diff", String(BLOCK_TINT_DIFF_DEFAULT)),
      settings.set("block_gradient", "true"),
      settings.set("subtask_rollup", "two_way"),
      settings.set("matrix_scope", "tasks_events"),
      settings.set("deadline_urgency", String(DEADLINE_URGENCY_DEFAULT.enabled)),
      settings.set("deadline_urgency_high_days", String(DEADLINE_URGENCY_DEFAULT.highWithinDays)),
      settings.set("deadline_urgency_medium_days", String(DEADLINE_URGENCY_DEFAULT.mediumWithinDays)),
      settings.set("mobile_topic_display", "icons+text"),
      settings.set("mobile_topic_multi", "true"),
      settings.set("desktop_form_variant", "paper"),
      settings.set("show_calendar_swipe_hint", "true"),
      settings.set("mobile_screenshot_feedback", "true"),
      settings.set("touch_pan_amp", String(TOUCH_PAN_AMP_DEFAULT)),
      settings.set("touch_pan_fling_ms", String(TOUCH_FLING_MS_DEFAULT)),
      settings.set("touch_pan_flip_px", String(TOUCH_FLIP_PX_DEFAULT)),
      settings.set("touch_pan_max_step", String(TOUCH_MAX_STEP_DEFAULT)),
    ]);
    await touchPreferencesUpdatedAt();
    // A Today setting changed while this larger reset was being persisted.
    // Its later lane write wins in storage; do not let the reset's delayed
    // state commit overwrite that newer optimistic choice in memory.
    if (agendaDaysRevision !== agendaDaysResetRevision) delete patch.agendaDays;
    if (agendaShowEmptyDaysRevision !== agendaShowEmptyResetRevision) delete patch.agendaShowEmptyDays;
    set(patch);
    useCalendarNavStore.getState().clearAnchors(["week", "days"]);
  },

  async setShortcut(action: ShortcutAction, key: string) {
    const next = { ...useSettingsStore.getState().shortcuts, [action]: key };
    await getServices().settings.set("shortcuts", JSON.stringify(next));
    await touchPreferencesUpdatedAt();
    set({ shortcuts: next });
  },

  async resetShortcuts() {
    await getServices().settings.set("shortcuts", JSON.stringify(DEFAULT_SHORTCUTS));
    await touchPreferencesUpdatedAt();
    set({ shortcuts: DEFAULT_SHORTCUTS });
  },

  async setSidebarVisible(part: SidebarPart, visible: boolean) {
    const next = { ...useSettingsStore.getState().sidebarVisible, [part]: visible };
    await getServices().settings.set("sidebar_visible", JSON.stringify(next));
    if (part === "priorities") {
      await getServices().settings.set("priorities_nav_visible_override", String(visible));
    }
    await touchPreferencesUpdatedAt();
    set({
      sidebarVisible: next,
      ...(part === "priorities" ? { prioritiesNavVisibleOverride: visible } : null),
    });
  },

  async resetPrioritiesNavVisibility() {
    await getServices().settings.delete("priorities_nav_visible_override");
    await touchPreferencesUpdatedAt();
    set({ prioritiesNavVisibleOverride: null });
  },

  async setSidebarNavOrder(order: SidebarPart[]) {
    const next = sanitizeSidebarOrder(order);
    set({ sidebarNavOrder: next });
    await getServices().settings.set("sidebar_nav_order", JSON.stringify(next));
    await touchPreferencesUpdatedAt();
  },

  async setSidebarCollapsed(section: SidebarSectionKey, collapsed: boolean) {
    const next = { ...useSettingsStore.getState().sidebarCollapsed, [section]: collapsed };
    await getServices().settings.set("sidebar_collapsed", JSON.stringify(next));
    await touchPreferencesUpdatedAt();
    set({ sidebarCollapsed: next });
  },

  async setSidebarWidth(px: number) {
    const v = clampSidebarWidth(px);
    if (useSettingsStore.getState().sidebarWidth === v) return;
    // Set by a continuous drag gesture — state applies optimistically; persist
    // once after the gesture settles (same pattern as setCalendarHourHeight).
    set({ sidebarWidth: v });
    if (sidebarWidthPersist) clearTimeout(sidebarWidthPersist);
    sidebarWidthPersist = setTimeout(() => {
      sidebarWidthPersist = null;
      void getServices().settings.set("sidebar_width", String(v));
      void touchPreferencesUpdatedAt();
    }, 400);
  },

  async setSidebarOpen(open: boolean) {
    if (useSettingsStore.getState().sidebarOpen === open) return;
    set({ sidebarOpen: open });
    await getServices().settings.set("sidebar_open", String(open));
    await touchPreferencesUpdatedAt();
  },

  async setSidebarFilterOrder(order: FilterSectionKey[]) {
    const next = sanitizeFilterOrder(order);
    // Update state SYNCHRONOUSLY first: framer's <Reorder> drives this via
    // onReorder mid-drag and reads the new `values` back immediately — awaiting
    // the repo write before set() leaves `values` stale and the drag snaps back.
    set({ sidebarFilterOrder: next });
    await getServices().settings.set("sidebar_filter_order", JSON.stringify(next));
    await touchPreferencesUpdatedAt();
  },

  // Entry-order setters update state SYNCHRONOUSLY (framer <Reorder> reads the
  // new values back mid-drag — see setSidebarFilterOrder) then persist.
  async setTopicOrder(order: string[]) {
    set({ topicOrder: order });
    await getServices().settings.set("topic_order", JSON.stringify(order));
    await touchPreferencesUpdatedAt();
  },
  async setTopicChildOrder(parentId: string, order: string[]) {
    const next = { ...useSettingsStore.getState().topicChildOrder, [parentId]: order };
    set({ topicChildOrder: next });
    await getServices().settings.set("topic_child_order", JSON.stringify(next));
    await touchPreferencesUpdatedAt();
  },
  async setLabelOrder(order: string[]) {
    set({ labelOrder: order });
    await getServices().settings.set("label_order", JSON.stringify(order));
    await touchPreferencesUpdatedAt();
  },
  async setCalendarOrder(order: string[]) {
    set({ calendarOrder: order });
    await getServices().settings.set("calendar_order", JSON.stringify(order));
    await touchPreferencesUpdatedAt();
  },

  async setViewExcludedTopics(view, ids) {
    const key = viewExcludedStateKey(view, "topics");
    const settingKey = viewExcludedSettingKey(view, "topics");
    const next = sanitizeStringArray(ids);
    if (sameStringArray(useSettingsStore.getState()[key], next)) return;
    set({ [key]: next } as Pick<SettingsState, typeof key>);
    await getServices().settings.set(settingKey, JSON.stringify(next));
    await touchPreferencesUpdatedAt();
  },

  async setViewExcludedCalendars(view, ids) {
    const key = viewExcludedStateKey(view, "calendars");
    const settingKey = viewExcludedSettingKey(view, "calendars");
    const next = sanitizeStringArray(ids);
    if (sameStringArray(useSettingsStore.getState()[key], next)) return;
    set({ [key]: next } as Pick<SettingsState, typeof key>);
    await getServices().settings.set(settingKey, JSON.stringify(next));
    await touchPreferencesUpdatedAt();
  },

  async applyBackupPreferences(prefs, opts = {}) {
    if (!prefs || typeof prefs !== "object") return;
    const { settings } = getServices();
    const patch: Partial<SettingsState> = {};
    const incomingAgendaDays = prefs.agendaDays === undefined ? undefined : parseAgendaDays(prefs.agendaDays);
    const incomingAgendaShowEmpty = typeof prefs.agendaShowEmptyDays === "boolean"
      ? prefs.agendaShowEmptyDays
      : undefined;
    const incomingAgendaDaysRevision = incomingAgendaDays === undefined ? null : ++agendaDaysRevision;
    const incomingAgendaShowEmptyRevision = incomingAgendaShowEmpty === undefined
      ? null
      : ++agendaShowEmptyDaysRevision;
    // Queue these writes when the apply operation STARTS. The Today controls
    // use the same lane, so a user action invoked later is guaranteed to be
    // persisted later even while the rest of a large backup is still applying.
    const agendaWrite = incomingAgendaDays === undefined && incomingAgendaShowEmpty === undefined
      ? Promise.resolve()
      : persistAgendaPreference(async () => {
          if (incomingAgendaDays !== undefined) await settings.set("agenda_days", String(incomingAgendaDays));
          if (incomingAgendaShowEmpty !== undefined) {
            await settings.set("agenda_show_empty_days", String(incomingAgendaShowEmpty));
          }
        });
    // The rest of a large restore can fail before it reaches the await below.
    // Mark this eagerly-started lane write as observed while preserving the
    // original promise so a write failure still rejects when we do await it.
    void agendaWrite.catch(() => undefined);
    if (prefs.shortcuts) {
      const v = mergeRecord(DEFAULT_SHORTCUTS, JSON.stringify(prefs.shortcuts), (x) => typeof x === "string" && x.length > 0);
      await settings.set("shortcuts", JSON.stringify(v)); patch.shortcuts = v;
    }
    if (prefs.sidebarVisible) {
      const v = mergeRecord(DEFAULT_SIDEBAR_VISIBLE, JSON.stringify(prefs.sidebarVisible), (x) => typeof x === "boolean");
      await settings.set("sidebar_visible", JSON.stringify(v)); patch.sidebarVisible = v;
      // Before the nullable override existed, `false` was necessarily a manual
      // hide (the old default was true). Preserve that intent on restore.
      if (prefs.prioritiesNavVisibleOverride === undefined && v.priorities === false) {
        await settings.set("priorities_nav_visible_override", "false");
        patch.prioritiesNavVisibleOverride = false;
      }
    }
    if (prefs.prioritiesNavVisibleOverride === null) {
      await settings.delete("priorities_nav_visible_override");
      patch.prioritiesNavVisibleOverride = null;
    } else if (typeof prefs.prioritiesNavVisibleOverride === "boolean") {
      await settings.set("priorities_nav_visible_override", String(prefs.prioritiesNavVisibleOverride));
      patch.prioritiesNavVisibleOverride = prefs.prioritiesNavVisibleOverride;
    }
    if (prefs.sidebarNavOrder) {
      const v = sanitizeSidebarOrder(prefs.sidebarNavOrder as SidebarPart[]);
      await settings.set("sidebar_nav_order", JSON.stringify(v)); patch.sidebarNavOrder = v;
    }
    if (prefs.sidebarCollapsed) {
      const v = mergeRecord(DEFAULT_SIDEBAR_COLLAPSED, JSON.stringify(prefs.sidebarCollapsed), (x) => typeof x === "boolean");
      await settings.set("sidebar_collapsed", JSON.stringify(v)); patch.sidebarCollapsed = v;
    }
    if (prefs.sidebarFilterOrder) {
      const v = sanitizeFilterOrder(prefs.sidebarFilterOrder as FilterSectionKey[]);
      await settings.set("sidebar_filter_order", JSON.stringify(v)); patch.sidebarFilterOrder = v;
    }
    for (const [key, storeKey, settingKey] of [
      ["topicOrder", "topicOrder", "topic_order"],
      ["labelOrder", "labelOrder", "label_order"],
      ["calendarOrder", "calendarOrder", "calendar_order"],
    ] as const) {
      const incoming = (prefs as Record<string, unknown>)[key];
      if (Array.isArray(incoming) && incoming.every((x) => typeof x === "string")) {
        await settings.set(settingKey, JSON.stringify(incoming));
        (patch as Record<string, unknown>)[storeKey] = incoming;
      }
    }
    if (prefs.topicChildOrder && typeof prefs.topicChildOrder === "object") {
      await settings.set("topic_child_order", JSON.stringify(prefs.topicChildOrder));
      patch.topicChildOrder = parseStringRecord(JSON.stringify(prefs.topicChildOrder));
    }
    for (const [prefKey, storeKey, settingKey] of [
      ["monthExcludedTopicIds", "monthExcludedTopicIds", "month_excluded_topic_ids"],
      ["monthExcludedCalendarIds", "monthExcludedCalendarIds", "month_excluded_calendar_ids"],
      ["todayExcludedTopicIds", "todayExcludedTopicIds", "today_excluded_topic_ids"],
      ["todayExcludedCalendarIds", "todayExcludedCalendarIds", "today_excluded_calendar_ids"],
      ["prioritiesExcludedTopicIds", "prioritiesExcludedTopicIds", "priorities_excluded_topic_ids"],
    ] as const) {
      const incoming = (prefs as Record<string, unknown>)[prefKey];
      if (Array.isArray(incoming) && incoming.every((x) => typeof x === "string")) {
        const v = sanitizeStringArray(incoming);
        await settings.set(settingKey, JSON.stringify(v));
        patch[storeKey] = v;
      }
    }
    await applyScalarPrefs(prefs, settings, patch);
    await agendaWrite;
    if (
      incomingAgendaDays !== undefined
      && incomingAgendaDaysRevision !== null
      && agendaDaysRevision === incomingAgendaDaysRevision
    ) {
      patch.agendaDays = incomingAgendaDays;
    }
    if (
      incomingAgendaShowEmpty !== undefined
      && incomingAgendaShowEmptyRevision !== null
      && agendaShowEmptyDaysRevision === incomingAgendaShowEmptyRevision
    ) {
      patch.agendaShowEmptyDays = incomingAgendaShowEmpty;
    }
    if (Object.keys(patch).length > 0 && opts.touch !== false) await touchPreferencesUpdatedAt();
    if (Object.keys(patch).length > 0) set(patch);
    if (patch.multiDayFocus) useCalendarNavStore.getState().clearAnchors(["week", "days"]);
  },
}));

async function hydrateSettingsOnce(
  set: (partial: Partial<SettingsState>) => void,
): Promise<void> {
  const raw = await getServices().settings.listAll();
  const setting = (key: string) => raw[key] ?? null;
  const wkRaw = setting("week_starts_on");
  const mdfRaw = setting("multi_day_focus");
  const tzRaw = setting("default_tz");
  const calRaw = setting("default_calendar_id");
  const tfRaw = setting("time_format");
  const dvRaw = setting("default_view");
  const drRaw = setting("default_reminder_minutes");
  const smhRaw = setting("snooze_morning_hour");
  const sehRaw = setting("snooze_evening_hour");
  const sahRaw = setting("snooze_afternoon_hour");
  const adRaw = setting("agenda_days");
  const asedRaw = setting("agenda_show_empty_days");
  const themeRaw = setting("theme");
  const languageRaw = setting("language");
  const textScaleRaw = setting("text_scale");
  const textFontRaw = setting("text_font");
  const lcvRaw = setting("last_calendar_view");
  const chhRaw = setting("calendar_hour_height");
  const cdcRaw = setting("calendar_day_count");
  const btdRaw = setting("block_tint_diff");
  const bgRaw = setting("block_gradient");
  const rollupRaw = setting("subtask_rollup");
  const msRaw = setting("matrix_scope");
  const duRaw = setting("deadline_urgency");
  const duhRaw = setting("deadline_urgency_high_days");
  const dumRaw = setting("deadline_urgency_medium_days");
  const mtdRaw = setting("mobile_topic_display");
  const mtmRaw = setting("mobile_topic_multi");
  const dfvRaw = setting("desktop_form_variant");
  const mshRaw = setting("mobile_sheet_height");
  const msfRaw = setting("mobile_screenshot_feedback");
  const scshRaw = setting("show_calendar_swipe_hint");
  const tpaRaw = setting("touch_pan_amp");
  const tpfRaw = setting("touch_pan_fling_ms");
  const tppRaw = setting("touch_pan_flip_px");
  const tpmRaw = setting("touch_pan_max_step");
  const deieRaw = setting("default_entry_is_event");
  const ddmRaw = setting("default_duration_min");
  const dermRaw = setting("default_event_reminder_minutes");
  const devRaw = setting("developer_mode");
  const obRaw = setting("onboarding_complete");
  const osRaw = setting("onboarding_step");
  const scRaw = setting("shortcuts");
  const svRaw = setting("sidebar_visible");
  const pnvoRaw = setting("priorities_nav_visible_override");
  const snoRaw = setting("sidebar_nav_order");
  const scollRaw = setting("sidebar_collapsed");
  const swRaw = setting("sidebar_width");
  const soRaw = setting("sidebar_open");
  const foRaw = setting("sidebar_filter_order");
  const toRaw = setting("topic_order");
  const tcoRaw = setting("topic_child_order");
  const loRaw = setting("label_order");
  const coRaw = setting("calendar_order");
  const metRaw = setting("month_excluded_topic_ids");
  const mecRaw = setting("month_excluded_calendar_ids");
  const tetRaw = setting("today_excluded_topic_ids");
  const tecRaw = setting("today_excluded_calendar_ids");
  const petRaw = setting("priorities_excluded_topic_ids");
  const sidebarVisible = mergeRecord(DEFAULT_SIDEBAR_VISIBLE, svRaw, (v) => typeof v === "boolean");
  const prioritiesNavVisibleOverride = pnvoRaw === "true"
    ? true
    : pnvoRaw === "false" || persistedSidebarPart(svRaw, "priorities") === false
      ? false
      : null;
  set({
    loaded: true,
    anthropicApiKey: setting("anthropic_api_key"),
    weekStartsOn: wkRaw === "0" ? 0 : 1,
    multiDayFocus: MULTI_DAY_FOCUSES.includes(mdfRaw as MultiDayFocus)
      ? (mdfRaw as MultiDayFocus)
      : "current_week",
    defaultTz: tzRaw == null ? systemTimeZone() : tzRaw === FLOATING ? null : tzRaw,
    defaultCalendarId: calRaw || null,
    timeFormat: tfRaw === "12h" ? "12h" : "24h",
    defaultView: DEFAULT_VIEWS.includes(dvRaw as DefaultView) ? (dvRaw as DefaultView) : "today",
    defaultReminderMinutes: parseMinutes(drRaw),
    snoozeMorningHour: clampInt(smhRaw, 0, 23, DEFAULT_SNOOZE_HOURS.morningHour),
    snoozeEveningHour: clampInt(sehRaw, 0, 23, DEFAULT_SNOOZE_HOURS.eveningHour),
    snoozeAfternoonHour: clampInt(sahRaw, 0, 23, SNOOZE_AFTERNOON_HOUR_DEFAULT),
    agendaDays: parseAgendaDays(adRaw),
    agendaShowEmptyDays: asedRaw === "true",
    theme: themeRaw === "light" || themeRaw === "dark" ? themeRaw : "system",
    language: isAppLanguage(languageRaw) ? languageRaw : DEFAULT_LANGUAGE,
    textScale: clampTextScale(textScaleRaw),
    textFont: isAppTextFont(textFontRaw) ? textFontRaw : TEXT_FONT_DEFAULT,
    lastCalendarView: CALENDAR_VIEWS.includes(lcvRaw as CalendarView) ? (lcvRaw as CalendarView) : "month",
    calendarHourHeight: clampInt(chhRaw, HOUR_HEIGHT_MIN, HOUR_HEIGHT_MAX, HOUR_HEIGHT_DEFAULT),
    calendarDayCount: clampInt(cdcRaw, DAY_COUNT_MIN, DAY_COUNT_MAX, DAY_COUNT_DEFAULT),
    blockTintDiff: clampFloat(btdRaw, 0, BLOCK_TINT_DIFF_MAX, BLOCK_TINT_DIFF_DEFAULT),
    blockGradient: bgRaw !== "false",
    subtaskRollup: ROLLUP_MODES.includes(rollupRaw as SubtaskRollup) ? (rollupRaw as SubtaskRollup) : "two_way",
    matrixScope: MATRIX_SCOPES.includes(msRaw as MatrixScope) ? (msRaw as MatrixScope) : "tasks_events",
    deadlineUrgency: duRaw == null ? DEADLINE_URGENCY_DEFAULT.enabled : duRaw !== "false",
    deadlineUrgencyHighDays: clampInt(duhRaw, DEADLINE_URGENCY_DAY_MIN, DEADLINE_URGENCY_DAY_MAX, DEADLINE_URGENCY_DEFAULT.highWithinDays),
    deadlineUrgencyMediumDays: clampInt(dumRaw, DEADLINE_URGENCY_DAY_MIN, DEADLINE_URGENCY_DAY_MAX, DEADLINE_URGENCY_DEFAULT.mediumWithinDays),
    mobileTopicDisplay: MOBILE_TOPIC_DISPLAYS.includes(mtdRaw as MobileTopicDisplay) ? (mtdRaw as MobileTopicDisplay) : "icons+text",
    mobileTopicMulti: mtmRaw == null ? true : mtmRaw !== "false",
    desktopFormVariant: DESKTOP_FORM_VARIANTS.includes(dfvRaw as DesktopFormVariant) ? (dfvRaw as DesktopFormVariant) : "paper",
    mobileSheetHeight: clampFloat(mshRaw, MOBILE_SHEET_HEIGHT_MIN, MOBILE_SHEET_HEIGHT_MAX, MOBILE_SHEET_HEIGHT_DEFAULT),
    mobileScreenshotFeedback: msfRaw == null ? true : msfRaw !== "false",
    showCalendarSwipeHint: scshRaw == null ? true : scshRaw !== "false",
    touchPanAmp: clampFloat(tpaRaw, TOUCH_PAN_AMP_MIN, TOUCH_PAN_AMP_MAX, TOUCH_PAN_AMP_DEFAULT),
    touchPanFlingMs: clampInt(tpfRaw, TOUCH_FLING_MS_MIN, TOUCH_FLING_MS_MAX, TOUCH_FLING_MS_DEFAULT),
    touchPanFlipPx: clampInt(tppRaw, TOUCH_FLIP_PX_MIN, TOUCH_FLIP_PX_MAX, TOUCH_FLIP_PX_DEFAULT),
    touchPanMaxStep: clampInt(tpmRaw, TOUCH_MAX_STEP_MIN, TOUCH_MAX_STEP_MAX, TOUCH_MAX_STEP_DEFAULT),
    defaultEntryIsEvent: deieRaw == null ? true : deieRaw !== "false",
    defaultDurationMin: clampInt(ddmRaw, DEFAULT_DURATION_MIN, DEFAULT_DURATION_MAX, DEFAULT_DURATION_DEFAULT),
    defaultEventReminderMinutes: parseMinutes(dermRaw),
    developerMode: devRaw === "true",
    onboardingComplete: obRaw === "true",
    onboardingStep: osRaw || null,
    shortcuts: mergeRecord(DEFAULT_SHORTCUTS, scRaw, (v) => typeof v === "string" && v.length > 0),
    sidebarVisible,
    prioritiesNavVisibleOverride,
    sidebarNavOrder: parseSidebarOrder(snoRaw),
    sidebarCollapsed: mergeRecord(DEFAULT_SIDEBAR_COLLAPSED, scollRaw, (v) => typeof v === "boolean"),
    sidebarWidth: clampSidebarWidth(swRaw),
    sidebarOpen: soRaw == null ? defaultSidebarOpen() : soRaw !== "false",
    sidebarFilterOrder: parseFilterOrder(foRaw),
    topicOrder: parseStringArray(toRaw),
    topicChildOrder: parseStringRecord(tcoRaw),
    labelOrder: parseStringArray(loRaw),
    calendarOrder: parseStringArray(coRaw),
    monthExcludedTopicIds: parseStringArray(metRaw),
    monthExcludedCalendarIds: parseStringArray(mecRaw),
    todayExcludedTopicIds: parseStringArray(tetRaw),
    todayExcludedCalendarIds: parseStringArray(tecRaw),
    prioritiesExcludedTopicIds: parseStringArray(petRaw),
  });
}

/** Restore the non-secret scalar settings from a backup's preferences block.
 *  Each value is validated exactly like hydrate() before being persisted. */
async function applyScalarPrefs(
  prefs: import("@app/core").BackupPreferences,
  settings: ReturnType<typeof getServices>["settings"],
  patch: Partial<SettingsState>,
): Promise<void> {
  if (prefs.weekStartsOn === 0 || prefs.weekStartsOn === 1) {
    await settings.set("week_starts_on", String(prefs.weekStartsOn));
    patch.weekStartsOn = prefs.weekStartsOn;
  }
  if (MULTI_DAY_FOCUSES.includes(prefs.multiDayFocus as MultiDayFocus)) {
    await settings.set("multi_day_focus", prefs.multiDayFocus!);
    patch.multiDayFocus = prefs.multiDayFocus as MultiDayFocus;
  }
  if (prefs.defaultTz !== undefined && (prefs.defaultTz === null || typeof prefs.defaultTz === "string")) {
    await settings.set("default_tz", prefs.defaultTz ?? FLOATING);
    patch.defaultTz = prefs.defaultTz;
  }
  if (prefs.defaultCalendarId === null || typeof prefs.defaultCalendarId === "string") {
    await settings.set("default_calendar_id", prefs.defaultCalendarId ?? "");
    patch.defaultCalendarId = prefs.defaultCalendarId;
  }
  if (prefs.timeFormat === "12h" || prefs.timeFormat === "24h") {
    await settings.set("time_format", prefs.timeFormat);
    patch.timeFormat = prefs.timeFormat;
  }
  if (DEFAULT_VIEWS.includes(prefs.defaultView as DefaultView)) {
    await settings.set("default_view", prefs.defaultView!);
    patch.defaultView = prefs.defaultView as DefaultView;
  }
  if (Array.isArray(prefs.defaultReminderMinutes) && prefs.defaultReminderMinutes.every((n) => typeof n === "number")) {
    await settings.set("default_reminder_minutes", JSON.stringify(prefs.defaultReminderMinutes));
    patch.defaultReminderMinutes = prefs.defaultReminderMinutes;
  }
  if (Array.isArray(prefs.defaultEventReminderMinutes) && prefs.defaultEventReminderMinutes.every((n) => typeof n === "number")) {
    await settings.set("default_event_reminder_minutes", JSON.stringify(prefs.defaultEventReminderMinutes));
    patch.defaultEventReminderMinutes = prefs.defaultEventReminderMinutes;
  }
  if (prefs.defaultDurationMin !== undefined) {
    const v = clampInt(prefs.defaultDurationMin, DEFAULT_DURATION_MIN, DEFAULT_DURATION_MAX, DEFAULT_DURATION_DEFAULT);
    await settings.set("default_duration_min", String(v));
    patch.defaultDurationMin = v;
  }
  if (typeof prefs.defaultEntryIsEvent === "boolean") {
    await settings.set("default_entry_is_event", String(prefs.defaultEntryIsEvent));
    patch.defaultEntryIsEvent = prefs.defaultEntryIsEvent;
  }
  if (prefs.snoozeMorningHour !== undefined) {
    const v = clampInt(prefs.snoozeMorningHour, 0, 23, DEFAULT_SNOOZE_HOURS.morningHour);
    await settings.set("snooze_morning_hour", String(v));
    patch.snoozeMorningHour = v;
  }
  if (prefs.snoozeEveningHour !== undefined) {
    const v = clampInt(prefs.snoozeEveningHour, 0, 23, DEFAULT_SNOOZE_HOURS.eveningHour);
    await settings.set("snooze_evening_hour", String(v));
    patch.snoozeEveningHour = v;
  }
  if (prefs.snoozeAfternoonHour !== undefined) {
    const v = clampInt(prefs.snoozeAfternoonHour, 0, 23, SNOOZE_AFTERNOON_HOUR_DEFAULT);
    await settings.set("snooze_afternoon_hour", String(v));
    patch.snoozeAfternoonHour = v;
  }
  if (prefs.theme === "light" || prefs.theme === "dark" || prefs.theme === "system") {
    await settings.set("theme", prefs.theme);
    patch.theme = prefs.theme;
  }
  if (isAppLanguage(prefs.language)) {
    await settings.set("language", prefs.language);
    patch.language = prefs.language;
  }
  if (prefs.textScale !== undefined) {
    const v = clampTextScale(prefs.textScale);
    await settings.set("text_scale", String(v));
    patch.textScale = v;
  }
  if (isAppTextFont(prefs.textFont)) {
    await settings.set("text_font", prefs.textFont);
    patch.textFont = prefs.textFont;
  }
  if (CALENDAR_VIEWS.includes(prefs.lastCalendarView as CalendarView)) {
    await settings.set("last_calendar_view", prefs.lastCalendarView!);
    patch.lastCalendarView = prefs.lastCalendarView as CalendarView;
  }
  if (ROLLUP_MODES.includes(prefs.subtaskRollup as SubtaskRollup)) {
    await settings.set("subtask_rollup", prefs.subtaskRollup!);
    patch.subtaskRollup = prefs.subtaskRollup as SubtaskRollup;
  }
  if (prefs.calendarHourHeight !== undefined) {
    const v = clampInt(prefs.calendarHourHeight, HOUR_HEIGHT_MIN, HOUR_HEIGHT_MAX, HOUR_HEIGHT_DEFAULT);
    await settings.set("calendar_hour_height", String(v));
    patch.calendarHourHeight = v;
  }
  if (prefs.calendarDayCount !== undefined) {
    const v = clampInt(prefs.calendarDayCount, DAY_COUNT_MIN, DAY_COUNT_MAX, DAY_COUNT_DEFAULT);
    await settings.set("calendar_day_count", String(v));
    patch.calendarDayCount = v;
  }
  if (prefs.blockTintDiff !== undefined) {
    const v = clampFloat(prefs.blockTintDiff, 0, BLOCK_TINT_DIFF_MAX, BLOCK_TINT_DIFF_DEFAULT);
    await settings.set("block_tint_diff", String(v));
    patch.blockTintDiff = v;
  }
  if (typeof prefs.blockGradient === "boolean") {
    await settings.set("block_gradient", String(prefs.blockGradient));
    patch.blockGradient = prefs.blockGradient;
  }
  if (prefs.sidebarWidth !== undefined) {
    const v = clampSidebarWidth(prefs.sidebarWidth);
    await settings.set("sidebar_width", String(v));
    patch.sidebarWidth = v;
  }
  if (typeof prefs.sidebarOpen === "boolean") {
    await settings.set("sidebar_open", String(prefs.sidebarOpen));
    patch.sidebarOpen = prefs.sidebarOpen;
  }
  if (MATRIX_SCOPES.includes(prefs.matrixScope as MatrixScope)) {
    await settings.set("matrix_scope", prefs.matrixScope!);
    patch.matrixScope = prefs.matrixScope as MatrixScope;
  }
  if (typeof prefs.deadlineUrgency === "boolean") {
    await settings.set("deadline_urgency", String(prefs.deadlineUrgency));
    patch.deadlineUrgency = prefs.deadlineUrgency;
  }
  if (prefs.deadlineUrgencyHighDays !== undefined) {
    const v = clampInt(prefs.deadlineUrgencyHighDays, DEADLINE_URGENCY_DAY_MIN, DEADLINE_URGENCY_DAY_MAX, DEADLINE_URGENCY_DEFAULT.highWithinDays);
    await settings.set("deadline_urgency_high_days", String(v));
    patch.deadlineUrgencyHighDays = v;
  }
  if (prefs.deadlineUrgencyMediumDays !== undefined) {
    const v = clampInt(prefs.deadlineUrgencyMediumDays, DEADLINE_URGENCY_DAY_MIN, DEADLINE_URGENCY_DAY_MAX, DEADLINE_URGENCY_DEFAULT.mediumWithinDays);
    await settings.set("deadline_urgency_medium_days", String(v));
    patch.deadlineUrgencyMediumDays = v;
  }
  if (MOBILE_TOPIC_DISPLAYS.includes(prefs.mobileTopicDisplay as MobileTopicDisplay)) {
    await settings.set("mobile_topic_display", prefs.mobileTopicDisplay!);
    patch.mobileTopicDisplay = prefs.mobileTopicDisplay as MobileTopicDisplay;
  }
  if (typeof prefs.mobileTopicMulti === "boolean") {
    await settings.set("mobile_topic_multi", String(prefs.mobileTopicMulti));
    patch.mobileTopicMulti = prefs.mobileTopicMulti;
  }
  if (DESKTOP_FORM_VARIANTS.includes(prefs.desktopFormVariant as DesktopFormVariant)) {
    await settings.set("desktop_form_variant", prefs.desktopFormVariant!);
    patch.desktopFormVariant = prefs.desktopFormVariant as DesktopFormVariant;
  }
  if (prefs.mobileSheetHeight !== undefined) {
    const v = clampFloat(prefs.mobileSheetHeight, MOBILE_SHEET_HEIGHT_MIN, MOBILE_SHEET_HEIGHT_MAX, MOBILE_SHEET_HEIGHT_DEFAULT);
    await settings.set("mobile_sheet_height", String(v));
    patch.mobileSheetHeight = v;
  }
  if (typeof prefs.mobileScreenshotFeedback === "boolean") {
    await settings.set("mobile_screenshot_feedback", String(prefs.mobileScreenshotFeedback));
    patch.mobileScreenshotFeedback = prefs.mobileScreenshotFeedback;
  }
  if (typeof prefs.showCalendarSwipeHint === "boolean") {
    await settings.set("show_calendar_swipe_hint", String(prefs.showCalendarSwipeHint));
    patch.showCalendarSwipeHint = prefs.showCalendarSwipeHint;
  }
  if (typeof prefs.onboardingComplete === "boolean") {
    await settings.set("onboarding_complete", String(prefs.onboardingComplete));
    patch.onboardingComplete = prefs.onboardingComplete;
  }
  if (prefs.onboardingStep === null || typeof prefs.onboardingStep === "string") {
    await settings.set("onboarding_step", prefs.onboardingStep ?? "");
    patch.onboardingStep = prefs.onboardingStep;
  }
}

/** Keep only the valid filter keys (unique), then append any missing ones so the
 *  result is always the full set in the user's order. */
function sanitizeFilterOrder(order: unknown): FilterSectionKey[] {
  const valid = new Set(DEFAULT_FILTER_ORDER);
  const seen = new Set<FilterSectionKey>();
  const out: FilterSectionKey[] = [];
  if (Array.isArray(order)) {
    for (const k of order) {
      if (valid.has(k as FilterSectionKey) && !seen.has(k as FilterSectionKey)) {
        seen.add(k as FilterSectionKey);
        out.push(k as FilterSectionKey);
      }
    }
  }
  for (const k of DEFAULT_FILTER_ORDER) if (!seen.has(k)) out.push(k);
  return out;
}

function parseFilterOrder(raw: string | null): FilterSectionKey[] {
  if (!raw) return DEFAULT_FILTER_ORDER;
  try {
    return sanitizeFilterOrder(JSON.parse(raw));
  } catch {
    return DEFAULT_FILTER_ORDER;
  }
}

/** Keep only valid sidebar part keys (unique), then append missing defaults. */
function sanitizeSidebarOrder(order: unknown): SidebarPart[] {
  const valid = new Set(DEFAULT_SIDEBAR_ORDER);
  const seen = new Set<SidebarPart>();
  const out: SidebarPart[] = [];
  if (Array.isArray(order)) {
    for (const k of order) {
      if (valid.has(k as SidebarPart) && !seen.has(k as SidebarPart)) {
        seen.add(k as SidebarPart);
        out.push(k as SidebarPart);
      }
    }
  }
  for (const k of DEFAULT_SIDEBAR_ORDER) if (!seen.has(k)) out.push(k);
  return out;
}

function parseSidebarOrder(raw: string | null): SidebarPart[] {
  if (!raw) return DEFAULT_SIDEBAR_ORDER;
  try {
    return sanitizeSidebarOrder(JSON.parse(raw));
  } catch {
    return DEFAULT_SIDEBAR_ORDER;
  }
}

/** Parse a persisted JSON string[] (entry-order list), falling back to []. */
function parseStringArray(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const v = JSON.parse(raw);
    return Array.isArray(v) && v.every((x) => typeof x === "string") ? sanitizeStringArray(v) : [];
  } catch {
    return [];
  }
}

function sanitizeStringArray(ids: string[]): string[] {
  return [...new Set(ids.filter((id) => typeof id === "string" && id.length > 0))];
}

function sameStringArray(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}

function viewExcludedStateKey(view: TopicExcludableView, kind: "topics"): "monthExcludedTopicIds" | "todayExcludedTopicIds" | "prioritiesExcludedTopicIds";
function viewExcludedStateKey(view: ExcludableView, kind: "calendars"): "monthExcludedCalendarIds" | "todayExcludedCalendarIds";
function viewExcludedStateKey(view: TopicExcludableView, kind: "topics" | "calendars") {
  if (view === "month") return kind === "topics" ? "monthExcludedTopicIds" : "monthExcludedCalendarIds";
  if (view === "today") return kind === "topics" ? "todayExcludedTopicIds" : "todayExcludedCalendarIds";
  return "prioritiesExcludedTopicIds";
}

function viewExcludedSettingKey(view: TopicExcludableView, kind: "topics" | "calendars"): string {
  return `${view}_excluded_${kind === "topics" ? "topic" : "calendar"}_ids`;
}

/** Parse a persisted JSON Record<string, string[]> (per-parent order map), falling back to {}. */
function parseStringRecord(raw: string | null): Record<string, string[]> {
  if (!raw) return {};
  try {
    const v = JSON.parse(raw);
    if (!v || typeof v !== "object" || Array.isArray(v)) return {};
    const out: Record<string, string[]> = {};
    for (const [k, val] of Object.entries(v)) {
      if (Array.isArray(val) && val.every((x) => typeof x === "string")) out[k] = val;
    }
    return out;
  } catch { return {}; }
}

/** Parse a JSON object from storage and merge its valid entries over `defaults`.
 *  Unknown keys are ignored; values failing `valid` fall back to the default. */
function mergeRecord<K extends string, V>(
  defaults: Record<K, V>,
  raw: string | null,
  valid: (v: unknown) => boolean,
): Record<K, V> {
  if (!raw) return defaults;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return defaults;
    const out = { ...defaults };
    for (const k of Object.keys(defaults) as K[]) {
      if (valid((parsed as Record<string, unknown>)[k])) out[k] = (parsed as Record<string, V>)[k]!;
    }
    return out;
  } catch {
    return defaults;
  }
}

/** Read one explicitly persisted sidebar flag without applying defaults. */
function persistedSidebarPart(raw: string | null, part: SidebarPart): boolean | null {
  if (!raw) return null;
  try {
    const value = (JSON.parse(raw) as Record<string, unknown>)[part];
    return typeof value === "boolean" ? value : null;
  } catch {
    return null;
  }
}

/** Parse the JSON-array minutes setting, falling back to [0] (at due time). */
function parseMinutes(raw: string | null): number[] {
  if (!raw) return [0];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((n) => typeof n === "number")) return parsed;
  } catch {
    /* fall through */
  }
  return [0];
}
